import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A WWI officer — a rig-compatible figure on the same skeleton and proportions
// as the standard actor (`worker-simple`) and the infantryman, so every
// interaction and station program fits it unchanged. Its parts carry the actor
// rig names (`body`, `head`, `arm-l`, `arm-r`, `leg-l`, `leg-r`; the whole
// figure moves via the rig's `root`). Modelled facing +Z, feet on the floor.
//
// Look: a longer field-grey greatcoat with a skirt over the leg tops, a Sam
// Browne belt with a diagonal chest strap, tall riding boots, a peaked cap
// instead of a helmet, and a rolled map in the right hand. No rifle, no pack.
// The coat (torso, skirt and sleeves) is tagged `slot: "team"`, so the field
// grey is only the default and a consumer recolours the uniform per side.
const COAT = "#6d6f64";       // field-grey greatcoat (the `team` slot's default)
const COAT_DARK = "#5c5e54";  // cap band / trousers
const BOOT = "#2e2620";       // tall polished riding boots
const SKIN = "#d9a06b";
const LEATHER = "#4a3a28";    // Sam Browne belt and strap
const MAP = "#e8e4da";        // rolled map / baton
const VISOR = "#33302a";      // cap visor

const LEG_H = 0.16;
const BODY_Y = LEG_H + 0.11;       // torso centre, stacked on the legs
const SHOULDER_Y = BODY_Y + 0.07;  // arm hinge height
const HEAD_Y = BODY_Y + 0.12 + 0.06;

// Animation pivots (model-local): neck, torso centre, shoulders, hips.
const E = "sine" as const;
const HP = [0, HEAD_Y - 0.05, 0] as const;
const BP = [0, BODY_Y, 0] as const;
const ALP = [-0.115, SHOULDER_Y, 0] as const;
const ARP = [0.115, SHOULDER_Y, 0] as const;
const LLP = [-0.05, LEG_H, 0] as const;
const LRP = [0.05, LEG_H, 0] as const;

// A trousered leg in a tall riding boot.
function leg(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.07, 0.06, 0.08), color: COAT_DARK, position: [x, 0.13, 0], name },
    { geometry: new THREE.BoxGeometry(0.072, 0.1, 0.082), color: BOOT, position: [x, 0.05, 0], name },
    { geometry: new THREE.BoxGeometry(0.075, 0.04, 0.11), color: BOOT, position: [x, 0.02, 0.015], name },
  ];
}

// A greatcoat sleeve hung at the shoulder with a bare hand at the cuff.
function arm(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.045, 0.18, 0.05), color: COAT, position: [x, SHOULDER_Y - 0.07, 0], name, slot: "team" },
    { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.045), color: SKIN, position: [x, SHOULDER_Y - 0.175, 0], name },
  ];
}

function officerParts(): Part[] {
  return [
    ...leg("leg-l", -0.05),
    ...leg("leg-r", 0.05),
    // Field-grey greatcoat: torso plus a skirt falling over the leg tops.
    { geometry: new THREE.BoxGeometry(0.18, 0.22, 0.11), color: COAT, position: [0, BODY_Y, 0], name: "body", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.19, 0.13, 0.12), color: COAT, position: [0, LEG_H - 0.02, 0], name: "body", slot: "team" },
    // Sam Browne: waist belt plus the diagonal strap across the chest.
    { geometry: new THREE.BoxGeometry(0.185, 0.035, 0.115), color: LEATHER, position: [0, BODY_Y - 0.06, 0], name: "body" },
    { geometry: new THREE.BoxGeometry(0.035, 0.21, 0.012), color: LEATHER,
      position: [0.02, BODY_Y + 0.015, 0.052], rotation: [0, 0, 0.5], name: "body" },
    ...arm("arm-l", -0.115),
    ...arm("arm-r", 0.115),
    // A rolled map held at the right hand, tucked along the forearm.
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.14, 6), color: MAP,
      position: [0.115, SHOULDER_Y - 0.17, 0.035], rotation: [Math.PI / 2, 0, 0], name: "arm-r" },
    // Head under the peaked cap: dark band, wider flat crown, front visor.
    { geometry: new THREE.BoxGeometry(0.11, 0.11, 0.11), color: SKIN, position: [0, HEAD_Y, 0], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.062, 0.062, 0.032, 8), color: COAT_DARK,
      position: [0, HEAD_Y + 0.06, 0], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.08, 0.072, 0.028, 8), color: COAT,
      position: [0, HEAD_Y + 0.088, 0], name: "head", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.09, 0.012, 0.055), color: VISOR,
      position: [0, HEAD_Y + 0.048, 0.07], rotation: [0.12, 0, 0], name: "head" },
  ];
}

export const officer = defineModel({
  id: "officer",
  batchId: "wwi",
  author: "Fable 5",
  name: "Officer",
  tags: ["wwi", "character", "actor", "humanoid", "person", "soldier", "military", "officer"],
  summary: "A rig-compatible WWI officer — field-grey greatcoat, Sam Browne belt, peaked cap and a rolled map in hand.",
  description:
    "The Great War commanding figure: a rig-compatible actor on the standard " +
    "worker skeleton, in a longer field-grey greatcoat (the team-colour channel) " +
    "with a Sam Browne belt and chest strap, tall riding boots, a peaked cap " +
    "instead of a helmet and a rolled map in the right hand. No rifle, no pack. " +
    "Any interaction or station program that poses the standard actor fits it " +
    "unchanged.",
  footprint: 0.14,
  tiles: { w: 1, h: 1 },
  parts: officerParts,
  // Where a carried good is cradled in front of the chest, matching the other
  // avatars; the good is supplied by the consumer at bake time.
  sockets: [
    { id: "carry", position: [0, 0.24, 0.13], slotType: "carry", accepts: ["carriable"],
      description: "A good carried in front of the chest." },
  ],
  animations: [
    { id: "idle", label: "Idle", duration: 2.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0.02, 0, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0, 0.006, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, -0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, 0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "walk", label: "Walk", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.04, 0, 0.02] }, { t: 0.4, value: [0.04, 0, -0.02], ease: E }, { t: 0.8, value: [0.04, 0, 0.02], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.4, value: [-0.35, 0, 0], ease: E }, { t: 0.8, value: [0.35, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.4, value: [0.35, 0, 0], ease: E }, { t: 0.8, value: [-0.35, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.3, 0, 0] }, { t: 0.4, value: [0.3, 0, 0], ease: E }, { t: 0.8, value: [-0.3, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0.3, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.006, 0], ease: E }, { t: 0.4, value: [0, 0, 0], ease: E },
        { t: 0.6, value: [0, 0.006, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "march", label: "March", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.03] }, { t: 0.5, value: [0, 0, -0.03], ease: E }, { t: 1.0, value: [0, 0, 0.03], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.6, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0.5, value: [0, 0, 0] }, { t: 0.75, value: [-0.6, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.5, value: [-0.35, 0, 0], ease: E }, { t: 1.0, value: [0.35, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.5, value: [0.35, 0, 0], ease: E }, { t: 1.0, value: [-0.35, 0, 0], ease: E }] },
    ] },
    { id: "salute", label: "Salute", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.04, 0, 0], ease: E },
        { t: 1.1, value: [-0.04, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0.3, 0], ease: E },
        { t: 1.1, value: [-1.8, 0.3, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.05, 0, 0], ease: E },
        { t: 1.1, value: [0.05, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
    ] },
  ],
});
